/**
 * Declaração ANM ativa por barragem (GUPM-182) — TS puro, sem React/Supabase/DOM.
 *
 * A baixa técnica do RT grava um enquadramento ANM (Res. 220) na TARP encerrada
 * (`closeTarp` → `anm_enquadramento`). Este módulo deriva, por barragem, QUAL
 * enquadramento está VIGENTE — para o `computeStatusEstrutura` promover o
 * semáforo da estrutura (a única via de promoção a nível ANM — GUPM-172/176).
 *
 * Vigência por evento ("até nova baixa que normalize"): a baixa MAIS RECENTE por
 * `closedAt` de cada barragem define o estado ANM corrente. Enquadramento `null`
 * (baixa sem declaração) normaliza → a barragem some do mapa e volta ao cálculo
 * automático. Determinístico: nunca `new Date()` interno — ordena pelo `closedAt`
 * persistido (ISO 8601 ordena lexicograficamente).
 */
import type { DeclaracaoAnm } from '../estrutura/status';
import { nivelEstruturaFromEnquadramento, type EnquadramentoAnm } from './baixa';

/** Baixa encerrada — shape mínimo pra derivar a declaração ativa (desacopla do datasource). */
export interface BaixaEncerrada {
  damId: string | null;
  anmEnquadramento: EnquadramentoAnm | null;
  closedAt: string | null;
}

/**
 * Declaração ANM ativa por barragem (`damId` → nível declarado). Barragens sem
 * declaração vigente (normalizadas / sem baixa / sem `dam_id`) ficam AUSENTES do
 * mapa → `computeStatusEstrutura` cai no cálculo automático.
 */
export function declaracaoAnmAtivaPorBarragem(
  encerradas: readonly BaixaEncerrada[],
): Record<string, DeclaracaoAnm> {
  // 1) Baixa mais recente (por closedAt) de cada barragem — vigência por evento.
  const maisRecentePorDam = new Map<string, BaixaEncerrada>();
  for (const b of encerradas) {
    if (!b.damId) continue; // sem barragem: a Res. 220 é sobre a estrutura (nada a declarar)
    const atual = maisRecentePorDam.get(b.damId);
    // Estritamente maior (`> 0`): em empate de closedAt, mantém o primeiro da ordem
    // recebida. O chamador (`listBaixasEncerradas`) entrega ordenado por closed_at
    // desc, então "o primeiro" é determinístico dado o contrato do datasource.
    if (!atual || compareClosedAt(b.closedAt, atual.closedAt) > 0) {
      maisRecentePorDam.set(b.damId, b);
    }
  }

  // 2) Só a barragem cuja baixa mais recente TEM enquadramento fica promovida.
  //    Enquadramento null = baixa que normalizou → sem declaração ativa.
  const ativas: Record<string, DeclaracaoAnm> = {};
  for (const [damId, b] of maisRecentePorDam) {
    if (b.anmEnquadramento) {
      ativas[damId] = { nivel: nivelEstruturaFromEnquadramento(b.anmEnquadramento) };
    }
  }
  return ativas;
}

/**
 * Ordena por `closedAt`. A comparação lexicográfica vale porque o datasource
 * devolve todos os `closed_at` no mesmo formato/offset do Postgres (`+00:00`,
 * precisão uniforme) → ordem lexicográfica = ordem cronológica. `null` fica atrás
 * (mais antigo): uma TARP ENCERRADA real sempre grava `closed_at`, então `null`
 * só ocorreria numa linha malformada — o guard evita que ela mascare a baixa real.
 */
function compareClosedAt(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return a < b ? -1 : 1;
}
